const AWS_ALGORITHM = 'AWS4-HMAC-SHA256';

/**
 * Utility service for setting up MQTT connections to a Thing and thereby
 * accessing the /Shadow Document/ via the publish and subscribe operations
 * supported by the `Paho.MQTT` interface.
 *
 * The code related to setting up and signing the MQTT URL is straight from
 * Amazon documentation:
 *   http://docs.aws.amazon.com/iot/latest/developerguide/protocols.html
 */
export default class ShadowService {
    /* @ngInject */
    constructor($log, $q, AwsService, ManifestService, AuthService) {
        this.$log = $log;
        this.$q = $q;
        this.AwsService = AwsService;
        this.ManifestService = ManifestService;
        this.AuthService = AuthService;

        this.cryptoUtil = this.AwsService.AWS.util.crypto;

        this.selectedDomain = undefined;
    }

    /**
     * Generate a URL for connecting with Amazon IoT MQTT Broker using
     * cognito credentials for the currently logged in user.
     */
    connectionUrl() {
        const credentials = this.AwsService.AWS.config.credentials;
        return this.generateMqttUrl(credentials);
    }

    getSignatureKey(key, date, region, service) {
        const kDate = this.cryptoUtil.hmac(`AWS4${key}`, date, 'buffer');
        const kRegion = this.cryptoUtil.hmac(kDate, region, 'buffer');
        const kService = this.cryptoUtil.hmac(kRegion, service, 'buffer');
        const kCredentials = this.cryptoUtil.hmac(
            kService,
            'aws4_request',
            'buffer',
        );
        return kCredentials;
    }

    generateMqttUrl() {
        const { AccessKeyId, SecretKey, SessionToken } =
            this.AuthService.getLocalCredentials();

        const datetime = this.AwsService.AWS.util.date
            .iso8601(new Date())
            .replace(/[:-]|\.\d{3}/g, '');
        const date = datetime.substr(0, 8);

        const method = 'GET';
        const uri = '/mqtt';
        const service = 'iotdevicegateway';

        const credentialScope = `${date}/${this.ManifestService.region()}/${service}/aws4_request`;

        let canonicalQuerystring = `X-Amz-Algorithm=${AWS_ALGORITHM}`;
        canonicalQuerystring += `&X-Amz-Credential=${encodeURIComponent(
            `${AccessKeyId}/${credentialScope}`,
        )}`;
        canonicalQuerystring += `&X-Amz-Date=${datetime}`;
        canonicalQuerystring += '&X-Amz-SignedHeaders=host';

        const canonicalHeaders = `host:${this.ManifestService.iotEndpointATS()}\n`;
        const payloadHash = this.cryptoUtil.sha256('', 'hex');
        // eslint-disable-next-line max-len
        const canonicalRequest = `${method}\n${uri}\n${canonicalQuerystring}\n${canonicalHeaders}\nhost\n${payloadHash}`;

        const stringToSign = `${AWS_ALGORITHM}\n${datetime}\n${credentialScope}\n${this.cryptoUtil.sha256(
            canonicalRequest,
            'hex',
        )}`;
        const signingKey = this.getSignatureKey(
            SecretKey,
            date,
            this.ManifestService.region(),
            service,
        );
        const signature = this.cryptoUtil.hmac(signingKey, stringToSign, 'hex');

        canonicalQuerystring += `&X-Amz-Signature=${signature}`;
        canonicalQuerystring += `&X-Amz-Security-Token=${encodeURIComponent(
            SessionToken,
        )}`;

        return `wss://${this.ManifestService.manifest.IotEndpointATS}${uri}?${canonicalQuerystring}`;
    }

    connect(thingName) {
        return this.$q((resolve, reject) => {
            const url = this.generateMqttUrl();
            const client = new window.Paho.MQTT.Client(url, thingName);
            const connectOptions = {
                onSuccess: () => {
                    this.$log.log('MQTT connection setup successfully.');
                    resolve(client);
                },
                useSSL: true,
                timeout: 3,
                mqttVersion: 3,
                onFailure: () => {
                    this.$log.log('Failed to setup MQTT connection.');
                    reject();
                },
            };
            client.connect(connectOptions);
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/services/shadow.service.js